import React from 'react';

import { useNavigate } from 'react-router-dom';

import { reverse } from 'named-urls';

import { observer } from 'mobx-react';

import { HomeSectionHeader, HomeSectionHeaderContainer } from 'containers/Home/styles';

import { Button } from 'components/Button';
import { useStore } from 'utils/hooks/store';

import ExpandMoreFilled from 'components/Icons/ExpandMoreFilled';
import AddActionModal from 'components/SharedFormComponents/AddTaskModal';
import routes from 'core/routes';

import { UserRole } from 'core/constants';

import {
  CreateNewTaskButton,
  EmptyTasksListContainer,
  EmptyTasksListMessage,
  StyledEmptyTasksListIcon,
  TaskListCell,
  TaskListCellStatusChip,
  TaskListCellTitle,
  TaskSummaryCell,
  TaskSummaryRow,
  TasksContainer,
  TasksListContainer,
  TasksSummaryContainer,
  TasksSummaryContent,
  TasksSummaryHeader,
} from './styles';
import TaskListCellDueDate from './components/TaskListCellDueDate';
import TasksPaneSkeleton from './TasksPane.skeleton';

const TasksPane: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { home, session } = useStore();
  const [addActionModalOpened, setAddActionModalOpened] = React.useState(false);

  if (home.content === undefined) {
    if (!home.hasError && home.isLoading) {
      return <TasksPaneSkeleton />;
    }

    if (home.hasError && !home.isLoading) {
      return null;
    }
  }

  if (!home.content) {
    return null;
  }

  const viewAllTasks = (): void => {
    navigate(routes.dashboard.tasks.list.toString());
  };

  const viewTask = (uuid: string): void => {
    navigate(reverse(routes.dashboard.tasks.details.toString(), { taskId: uuid }));
  };

  const createNewTask = (): void => {
    setAddActionModalOpened(true);
  };

  const onCloseAddActionModal = async (): Promise<void> => {
    await home.loadHomeContent();
    setAddActionModalOpened(false);
  };

  return (
    <>
      <HomeSectionHeaderContainer>
        <HomeSectionHeader>My Actions</HomeSectionHeader>
        {home.content.recentTasks !== undefined && home.content.recentTasks.length > 0 ? (
          <Button variant="outlined" onClick={viewAllTasks}>
            View all
          </Button>
        ) : null}
      </HomeSectionHeaderContainer>
      <TasksContainer>
        <TasksSummaryContainer>
          <TaskSummaryRow>
            <TaskSummaryCell>
              <TasksSummaryHeader>Actions assigned to me</TasksSummaryHeader>
              <TasksSummaryContent>
                {home.actionsSummary?.assignedToUser !== undefined ? home.actionsSummary.assignedToUser : 'N/A'}
              </TasksSummaryContent>
            </TaskSummaryCell>
            <TaskSummaryCell>
              <TasksSummaryHeader>Actions in progress</TasksSummaryHeader>
              <TasksSummaryContent>
                {home.actionsSummary?.inProgress !== undefined ? home.actionsSummary.inProgress : 'N/A'}
              </TasksSummaryContent>
            </TaskSummaryCell>
          </TaskSummaryRow>
          <TaskSummaryRow>
            <TaskSummaryCell>
              <TasksSummaryHeader>Actions due this week</TasksSummaryHeader>
              <TasksSummaryContent>
                {home.actionsSummary?.dueThisWeek !== undefined ? home.actionsSummary.dueThisWeek : 'N/A'}
              </TasksSummaryContent>
            </TaskSummaryCell>
            <TaskSummaryCell>
              <TasksSummaryHeader>Actions completed this week</TasksSummaryHeader>
              <TasksSummaryContent>
                {home.actionsSummary?.completedThisWeek !== undefined ? home.actionsSummary.completedThisWeek : 'N/A'}
              </TasksSummaryContent>
            </TaskSummaryCell>
          </TaskSummaryRow>
        </TasksSummaryContainer>
        {home.content.recentTasks === undefined || home.content.recentTasks.length === 0 ? (
          <EmptyTasksListContainer>
            <StyledEmptyTasksListIcon />
            <EmptyTasksListMessage>You have no outstanding actions</EmptyTasksListMessage>
            {/* button to create new task is only visible for non-users */}
            {session.user?.role !== UserRole.User && (
              <CreateNewTaskButton onClick={createNewTask}>Create New</CreateNewTaskButton>
            )}
            {addActionModalOpened && <AddActionModal onClose={onCloseAddActionModal} />}
          </EmptyTasksListContainer>
        ) : (
          <TasksListContainer>
            {home.content.recentTasks.map((task) => (
              <TaskListCell
                key={task.uuid}
                onClick={() => {
                  viewTask(task.uuid);
                }}
              >
                <TaskListCellStatusChip status={task.status} />
                <TaskListCellTitle>{task.name}</TaskListCellTitle>
                <TaskListCellDueDate dueDate={task.dueDate} />
                <ExpandMoreFilled />
              </TaskListCell>
            ))}
          </TasksListContainer>
        )}
      </TasksContainer>
    </>
  );
};

export default observer(TasksPane);
